<template>
  <div class="boss-page">
    <header class="boss-header">
      <van-icon name="arrow-left" @click="$router.back()"/>
      <span>部门数据</span>
      <i></i>
    </header>

    <!-- 事业部选择（所有 Tab 统一显示） -->
    <div class="unit-bar" @click="showPicker = true">
      <span>{{ currentUnit || '选择事业部' }}</span>
      <van-icon name="arrow-down" class="arrow"/>
    </div>

    <!-- Tab -->
    <div class="tabs">
      <button v-for="tab in tabs" :key="tab.code" :class="{active: activeTab === tab.code}" @click="activeTab = tab.code">
        {{ tab.label }}
      </button>
    </div>

    <!-- 日志情况 -->
    <template v-if="activeTab === 'LOG'">
      <div class="date-bar">
        <van-icon name="arrow-left" @click="shiftDate(-1)"/>
        <strong>{{ dateLabel }}</strong>
        <van-icon name="arrow" @click="shiftDate(1)"/>
      </div>

      <div v-if="loading" class="state">加载中...</div>
      <div v-else-if="error" class="state error">{{ error }}</div>
      <template v-else>
        <!-- 4 个指标 -->
        <div class="metrics">
          <div class="metric-item red">
            <div class="metric-circle">
              <strong>{{ stats.checkedOut }}</strong><small>/{{ stats.total }}</small>
            </div>
            <span>已下班</span>
          </div>
          <div class="metric-item purple">
            <div class="metric-circle">
              <strong>{{ stats.submitted }}</strong><small>/{{ stats.total }}</small>
            </div>
            <span>日志提交</span>
          </div>
          <div class="metric-item blue">
            <div class="metric-circle">
              <strong>{{ stats.avg.toFixed(0) }}</strong>
            </div>
            <span>平均分</span>
          </div>
          <div class="metric-item green">
            <div class="metric-circle">
              <strong>{{ unitRank }}</strong>
            </div>
            <span>部门排名</span>
          </div>
        </div>

        <!-- 榜单 -->
        <div class="ranking-list" v-if="currentRows.length">
          <section class="rank-group">
            <h3>{{ currentUnit }}：</h3>
            <article v-for="(row, index) in currentRows" :key="row.employeeName + index" class="rank-row">
              <b class="rank-no">{{ index + 1 }}</b>
              <span class="avatar">{{ (row.employeeName || '?').slice(0, 1) }}</span>
              <div class="person">
                <strong>{{ row.employeeName || '未命名员工' }}</strong>
                <small>{{ row.departmentName || '-' }} · {{ row.positionName || '-' }}</small>
              </div>
              <div class="metric">
                <strong>{{ scoreText(row.score) }}</strong>
                <small>综合分</small>
              </div>
            </article>
          </section>
        </div>
        <div v-else class="state">该事业部暂无数据</div>
      </template>
    </template>

    <!-- 销售业绩 -->
    <SalesPerformancePanel v-else-if="activeTab === 'SALES'" :date="date" :unit="currentUnit"/>

    <!-- 品牌数据 -->
    <BrandPerformancePanel v-else-if="activeTab === 'BRAND'" :date="date" :unit="currentUnit"/>

    <!-- 事业部选择弹窗 -->
    <van-popup v-model="showPicker" position="bottom" round class="unit-picker">
      <div class="picker-header">
        <span @click="showPicker = false">取消</span>
        <strong>选择事业部</strong>
        <span @click="confirmUnit">确认</span>
      </div>
      <div class="picker-body">
        <div v-for="u in units" :key="u" :class="['picker-item', {active: tempUnit === u}]" @click="tempUnit = u">
          {{ u }}
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import coreapi from '@/utils/coreapi';
import SalesPerformancePanel from './SalesPerformancePanel.vue';
import BrandPerformancePanel from './BrandPerformancePanel.vue';

export default {
  name: 'BossDepartmentData',
  components: { SalesPerformancePanel, BrandPerformancePanel },
  data() {
    const now = new Date();
    const queryDate = this.$route.query.date;
    const parsedDate = queryDate && /^\d{4}-\d{2}-\d{2}$/.test(queryDate)
      ? queryDate
      : this.localDate(now);
    return {
      date: parsedDate,
      activeTab: 'LOG',
      tabs: [
        { code: 'LOG', label: '日志情况' },
        { code: 'SALES', label: '销售业绩' },
        { code: 'BRAND', label: '品牌数据' }
      ],
      rows: [],
      loading: false,
      error: '',
      currentUnit: this.$route.query.businessUnit || '',
      tempUnit: '',
      showPicker: false
    };
  },
  computed: {
    dateLabel() {
      return this.date.replace(/-/g, '/');
    },
    units() {
      const set = new Set(this.rows.map(r => r.businessUnit).filter(Boolean));
      return Array.from(set).sort();
    },
    currentRows() {
      return this.rows.filter(r => r.businessUnit === this.currentUnit);
    },
    stats() {
      const rows = this.currentRows;
      const total = rows.length;
      const checkedOut = rows.filter(r => r.clockOutAt).length;
      const submitted = rows.filter(r => r.submittedAt).length;
      const scores = rows.map(r => r.score).filter(s => s != null);
      const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
      return { total, checkedOut, submitted, avg };
    },
    unitRank() {
      const unitStats = {};
      this.rows.forEach(r => {
        const unit = r.businessUnit || '其他';
        if (!unitStats[unit]) unitStats[unit] = { sum: 0, count: 0 };
        if (r.score != null) {
          unitStats[unit].sum += r.score;
          unitStats[unit].count += 1;
        }
      });
      const list = Object.keys(unitStats).map(u => ({
        unit: u,
        avg: unitStats[u].count ? unitStats[u].sum / unitStats[u].count : 0
      }));
      list.sort((a, b) => b.avg - a.avg);
      const idx = list.findIndex(x => x.unit === this.currentUnit);
      return idx >= 0 ? idx + 1 : '-';
    }
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const data = await coreapi.bossLogRankings(this.date, 'BUSINESS_UNIT');
        this.rows = data.rows || [];
        // 若未指定事业部，默认第一个
        if (!this.currentUnit && this.units.length) {
          this.currentUnit = this.units[0];
        }
      } catch (e) {
        this.error = e.message || '服务调用失败';
      } finally {
        this.loading = false;
      }
    },
    shiftDate(delta) {
      const d = new Date(this.date + 'T00:00:00');
      d.setDate(d.getDate() + delta);
      if (d > new Date()) return;
      this.date = this.localDate(d);
      this.load();
    },
    localDate(d) {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    scoreText(v) {
      return v === null || v === undefined ? '--' : Number(v).toFixed(1);
    },
    confirmUnit() {
      this.currentUnit = this.tempUnit || this.currentUnit;
      this.showPicker = false;
    }
  },
  watch: {
    showPicker(val) {
      if (val) this.tempUnit = this.currentUnit;
    }
  }
};
</script>

<style lang="less" scoped>
.boss-page {
  min-height: 100vh;
  background: #f6f7fb;
  color: #303133;
}

.boss-header {
  height: 52px;
  background: linear-gradient(90deg, #26191b, #c9272c);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 18px;
  font-size: 20px;

  i {
    width: 24px;
  }
}

.unit-bar {
  height: 50px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: #333;

  .arrow {
    margin-left: 6px;
    font-size: 14px;
    color: #999;
  }
}

.tabs {
  display: flex;
  background: #fff;
  border-top: 1px solid #f0f1f5;
  padding: 0 12px;

  button {
    flex: 1;
    border: 0;
    background: none;
    padding: 14px 4px;
    color: #697386;
    font-size: 15px;

    &.active {
      color: #d9272e;
      border-bottom: 2px solid #d9272e;
      font-weight: 600;
    }
  }
}

.date-bar {
  height: 58px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 28px;
  font-size: 17px;
  border-top: 1px solid #f0f1f5;
}

.metrics {
  display: flex;
  justify-content: space-around;
  padding: 20px 12px;

  .metric-item {
    display: flex;
    flex-direction: column;
    align-items: center;

    .metric-circle {
      width: 76px;
      height: 76px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font-size: 14px;

      strong {
        font-size: 26px;
        font-weight: 700;
      }

      small {
        font-size: 14px;
        margin-left: 2px;
      }
    }

    span {
      margin-top: 8px;
      font-size: 13px;
      color: #666;
    }

    &.red .metric-circle { background: linear-gradient(135deg, #ff7e7e, #ff4d4f); }
    &.purple .metric-circle { background: linear-gradient(135deg, #c084fc, #a855f7); }
    &.blue .metric-circle { background: linear-gradient(135deg, #7aa8ff, #5b7cff); }
    &.green .metric-circle { background: linear-gradient(135deg, #4ade80, #16a34a); }
  }
}

.ranking-list {
  padding: 0 12px 24px;
}

.rank-group {
  h3 {
    font-size: 15px;
    margin: 8px 4px;
    color: #41475a;
  }
}

.rank-row {
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 8px;
  padding: 12px 10px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(30, 35, 60, .05);
}

.rank-no {
  width: 22px;
  text-align: center;
  color: #e4a52f;
  font-size: 16px;
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #d9272e;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 10px 0 4px;
  font-size: 14px;
}

.person {
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    font-size: 15px;
    color: #303133;
  }

  small {
    display: block;
    color: #9aa1b2;
    margin-top: 4px;
    font-size: 12px;
  }
}

.metric {
  text-align: right;

  strong {
    display: block;
    font-size: 16px;
    color: #d9272e;
  }

  small {
    display: block;
    color: #9aa1b2;
    margin-top: 4px;
    font-size: 12px;
  }
}

.state {
  padding: 60px 20px;
  text-align: center;
  color: #9aa1b2;

  &.error {
    color: #d9272e;
  }
}

.unit-picker {
  .picker-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px;
    border-bottom: 1px solid #f0f1f5;

    span {
      font-size: 15px;
      color: #666;
    }

    strong {
      font-size: 16px;
      color: #333;
    }

    span:last-child {
      color: #d9272e;
    }
  }

  .picker-body {
    max-height: 320px;
    overflow-y: auto;
    padding: 8px 0;

    .picker-item {
      padding: 14px 16px;
      text-align: center;
      font-size: 15px;
      color: #333;
      border-bottom: 1px solid #f7f8fa;

      &.active {
        color: #d9272e;
        font-weight: 600;
        background: #fff5f5;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/boss/DepartmentData.vue